import { companyName } from "./companyName";
// Single source of truth for turning Finnhub's `finnhubIndustry` string into a
// sector name. One taxonomy, the SECTOR_COLORS names, for every screen.
//
// There used to be a second, narrower vocabulary here ("שבבים", "פארמה") that
// WhatIfSimulator spoke, and it was wrong twice over. Factually: Finnhub
// answers the broad "Technology" for AAPL, MSFT and ORCL, and projecting that
// onto the narrowest theme in the key called them chip companies — "Media"
// likewise made AT&T and Verizon social networks. Structurally: mixed
// resolutions break measurement. "טכנולוגיה 30% · שבבים 25%" hides that the
// real exposure is 55%, and any concentration figure computed on split slices
// reads lower than the truth.
//
// A narrow theme is only ever legitimate when the industry string itself names
// it, and Finnhub's taxonomy does not go that deep — so there is one set of
// names, and it is this one.

// Ordered — first match wins, and the order carries weight.
//
// MEDIA leads because Finnhub returns the literal "Media" for GOOGL, META, DIS
// and NFLX, and the rule this replaces tested the bare substring "med", which
// swallowed all four into פארמה. HEALTH sits ahead of TECH for the same class
// of reason: "Biotechnology" contains "tech", so ABBV, AMGN and REGN used to
// land in שבבים.
export const INDUSTRY_RULES = [
  ["MEDIA",    /media|telecom|communication|broadcast|entertainment|publish/],
  ["HEALTH",   /pharma|biotech|biochem|medical|medicine|health|life science/],
  ["TECH",     /semiconductor|technology|software|hardware|computer|internet|electronic/],
  ["CONSUMER", /retail|consumer|food|beverage|restaurant|apparel|hotel|household|textile|luxury|leisure|tobacco/],
  // Whole words since sprint 16 (4.2): the bare substring "auto" also
  // matched "Automation" and "Autonomous", and labelled them רכב.
  ["AUTO",     /\bauto(mobile|motive)?s?\b|\bvehicles?\b/],
  ["FINANCE",  /financ|bank|insur/],
  ["ENERGY",   /energy|oil|gas|petrol|utilities|coal/],
  // Three real Finnhub values matched nothing at all, which left CAT, GE, BA,
  // LMT, PLD and AMT permanently outside every breakdown — a portfolio holding
  // any of them showed partial coverage for good, with no way to resolve it.
  ["DEFENSE",  /aerospace|defense|defence/],
  // Materials in GICS, ahead of INDUSTRIAL's "construction".
  ["MATERIALS", /construction materials/],
  // Sprint 16, 4.2: five of Finnhub's industries matched nothing and showed
  // as "אחר": Commercial Services & Supplies, Electrical Equipment,
  // Professional Services, Road & Rail, Trading Companies & Distributors. All
  // five are industrials in GICS, the classification Finnhub's names follow.
  ["INDUSTRIAL", /machinery|industrial|construction|engineering|building|transportation|logistics|airline|marine|commercial services|electrical equipment|professional services|road & rail|railroad|trading compan/],
  // "Distributors" on its own is a consumer-discretionary industry in GICS.
  ["CONSUMER", /^distributors$/],
  ["REALESTATE", /real estate|reit/],
  // Materials in GICS: Chemicals, Metals & Mining, Packaging, Paper & Forest
  // (sprint 16, David). Last, on purpose: "Coal Mining" goes to ENERGY
  // and "Construction Materials" is caught by its own rule above.
  ["MATERIALS", /chemical|metals|mining|steel|alumin|packag|paper|forest/],
];

export function industryToKey(industry) {
  const s = String(industry || "").trim().toLowerCase();
  if (!s) return null;
  for (const [key, re] of INDUSTRY_RULES) if (re.test(s)) return key;
  return null;
}

// The sector names. The keys of SECTOR_COLORS in constants/data.js, except
// חומרי גלם (sprint 16), which has no colour of its own: no screen colours by
// SECTOR_COLORS today, and a name without a colour there falls back to a
// derived hue.
const SECTOR_LABELS = {
  MEDIA: "תקשורת",   HEALTH:  "בריאות",  TECH:   "טכנולוגיה",
  AUTO:  "רכב",      FINANCE: "פיננסים", ENERGY: "אנרגיה",
  CONSUMER: "צרכנות", DEFENSE: "ביטחון/חלל", INDUSTRIAL: "תעשייה",
  REALESTATE: "נדל\"ן", MATERIALS: "חומרי גלם",
};

export const SECTOR_ETF     = "תעודת סל";
export const SECTOR_UNKNOWN = "אחר";

// The sector name for an industry string. null when nothing matched, so the
// caller's own fallback chain keeps going — null is "not determined", never a
// bucket.
export function industryToSector(industry) {
  const key = industryToKey(industry);
  return key ? SECTOR_LABELS[key] : null;
}

// What a stock/profile2 response actually means.
//
// Finnhub answers HTTP 200 with a literal `{}` for anything that is not a
// company — IBIT and SPY both do, verified live. Every failure answers with a
// non-2xx instead: 401 for a bad key, 403 for an endpoint outside the free
// tier, 429 once the minute quota is spent. fetchProfile() already routes
// those apart, because `_get` in utils/finnhub.js throws on `!res.ok` and its
// catch returns null. So null here means "no answer" and `{}` means "answered,
// and it is not a company".
//
// Keeping those two apart is the point of this function. Both look like "no
// industry", and collapsing them would mean that the first minute the quota
// runs out, every position in the portfolio is stamped תעודת סל — wrong,
// permanent-looking, and silent. The `error` key is tested as well, so a quota
// reply that ever arrives as 200 with a body is still read as a failure.
export function readProfile(profile) {
  if (profile == null || typeof profile !== "object") return "ERROR";
  if (profile.error) return "ERROR";
  if (profile.finnhubIndustry) return "COMPANY";
  if (Object.keys(profile).length === 0) return "ETF";
  return "UNRESOLVED";
}

// Resolves one holding to { sector, industry }.
//
// A null sector means "not determined" and the caller must render nothing for
// it. "אחר" is a claim — that we asked and there is no bucket — and it is not
// earned while the request is in flight or after it failed.
// `name` rides along: the same profile2 answer carries the company's name,
// and the holding is created with its ticker in the name slot. The caller
// keeps the ticker when the profile has no name — never an empty string.
export async function classifySymbol(symbol, fetchProfileFn) {
  const profile = await fetchProfileFn(symbol);
  const kind = readProfile(profile);
  // Spaced as it should read (utils/companyName.js · sprint 17).
  const name = typeof profile?.name === "string" && profile.name.trim() ? companyName(profile.name) : null;
  if (kind === "ERROR") return { sector: null, industry: null, name: null };
  if (kind === "ETF")   return { sector: SECTOR_ETF, industry: null, name };
  const industry = profile.finnhubIndustry || null;
  return { sector: industryToSector(industry) || SECTOR_UNKNOWN, industry, name };
}
